import React, { useState } from 'react';
import { 
  Box, 
  TextField, 
  Button, 
  Typography, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem,
  Autocomplete,
  styled 
} from '@mui/material';
import FormDialog from '../../../../../components/Dialog/FormDialog';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import toast from 'react-hot-toast';
import { useHPDAffidavitCategories, useHPDViolationAffidavits } from '../../../../../reactQuery/violations';
import { createHPDAffidavit } from '../../../../../sevices/violationsApi';
import { useMutation } from '@tanstack/react-query';
import UploadIcon from '../../../../../styles/svg/upload-file.svg';
// Styled components for upload area
const UploadButton = styled(Button)(({ theme }) => ({
  border: `1px dashed ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(4),
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  backgroundColor: theme.palette.background.paper,
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
  boxShadow: "none",
  flexDirection: "column",
  gap: "8px"
}));

// Validation schema
const schema = yup.object({
  category: yup.string().required('Category is required'),
  title: yup.string().required('Title is required'),
  affidavit_name: yup.string().required('Affidavit name is required'),
  file: yup.mixed().required('File is required')
});

const AddAffidavitModal = ({ open, handleClose, onSuccess }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const { responseData: categoriesData = [], isLoading, isError, refetch } = useHPDAffidavitCategories();
    const {
      responseData: affidavitData = [],
      isLoading: isLoadingAffidavit,
      isError: isErrorAffidavit,
      refetch: refetchAffidavit,
    } = useHPDViolationAffidavits();

  const { mutate: createAffidavit, isPending } = useMutation({
    mutationFn: (data) => createHPDAffidavit(data),
  });
  
  // Categories for dropdown
  const categories = categoriesData?.map((category) => ({
    value: category.toLowerCase(),
    label: category.charAt(0).toUpperCase() + category.slice(1).toLowerCase(),
  }));



  // Get unique titles by filtering out duplicates
  const uniqueTitleOptions = Array.from(
    new Set(affidavitData?.map(item => item.title).filter(Boolean))
  ).map(title => ({
    value: title,
    label: title.charAt(0).toUpperCase() + title.slice(1).toLowerCase()
  }));


  const {
    control,
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      category: '',
      title: '',
      name: '',
      file: null
    }
  });

  // Mock upload mutation - replace with actual API
  const { mutate: uploadAffidavit } = useMutation({
    mutationFn: (data) => createHPDAffidavit(data),
    onSuccess: () => {
      reset();
      setSelectedFile(null);
      handleClose();
      if (onSuccess) onSuccess();
    },
    onError: () => {
      toast.error("Failed to upload affidavit");
    },
  });
    
 

  const onSubmit = (data) => {
    // Create form data for file upload
    const formData = new FormData();
    formData.append('category', data.category);
    formData.append('title', data.title);
    formData.append('affidavit_name', data.affidavit_name);
    formData.append('file', data.file);
    
    // Call the mutation with the form data
    uploadAffidavit(formData);
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setValue('file', file);
    }
  };

  const handleCancel = () => {
    if (!isPending) {
      reset();
      setSelectedFile(null);
      handleClose();
    }
  };

  return (
    <FormDialog
      open={open}
      handleClose={handleCancel}
      title="Add New Affidavit"
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Category Selection */}
        <Box sx={{ mb: 3 }}>
          <Controller
            name="category"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth error={!!errors.category}>
                <InputLabel id="category-label" shrink>
                  Category
                </InputLabel>
                <Select
                  {...field}
                  labelId="category-label"
                  label="Category"
                  disabled={isPending}
                  placeholder="Select Category"
                  className="select-box"
                >
                  {categories.map((category) => (
                    <MenuItem
                      key={category.value}
                      value={category.value}
                      className="dropdown-menu-options"
                    >
                      <p className="dropdown-options">{category.label}</p>
                    </MenuItem>
                  ))}
                </Select>
                {errors.category && (
                  <Typography variant="caption" color="error">
                    {errors.category.message}
                  </Typography>
                )}
              </FormControl>
            )}
          />
        </Box>

        {/* Title Selection - Now with Autocomplete */}
        <Box sx={{ mb: 3 }}>
          <Controller
            name="title"
            control={control}
            render={({ field: { onChange, value, ...field } }) => (
              <FormControl fullWidth error={!!errors.title}>
                <Autocomplete
                  {...field}
                  options={uniqueTitleOptions}
                  getOptionLabel={(option) => {
                    // Handle both string values and option objects
                    if (typeof option === "string") return option;
                    return option.label || "";
                  }}
                  isOptionEqualToValue={(option, value) => {
                    if (typeof value === "string")
                      return option.value === value || option.label === value;
                    return option.value === value?.value;
                  }}
                  value={value}
                  onChange={(_, newValue) => {
                    // Handle both string values and option objects
                    const titleValue =
                      typeof newValue === "string"
                        ? newValue
                        : newValue?.label || newValue?.value || "";
                    onChange(titleValue);
                  }}
                  onInputChange={(event, newInputValue) => {
                    // This handles the case when a user is typing a new value
                    if (event && event.type === "change") {
                      onChange(newInputValue);
                    }
                  }}
                  freeSolo
                  disabled={isPending}
                  className="select-box"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Title"
                      placeholder="Add/Select Title"
                      error={!!errors.title}
                      helperText={errors.title?.message}
                      InputLabelProps={{ shrink: true }}
                      onChange={(e) => {
                        // Direct input change also needs to update the form
                        onChange(e.target.value);
                      }}
                    />
                  )}
                />
              </FormControl>
            )}
          />
        </Box>

        {/* Affidavit Name */}
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            label="Affidavit Name"
            placeholder="Enter Affidavit Name"
            {...register("affidavit_name")}
            InputLabelProps={{ shrink: true }}
            error={!!errors.affidavit_name}
            helperText={errors.affidavit_name?.message}
            disabled={isPending}
          />
        </Box>

        {/* File Upload */}
        <Box sx={{ mb: 3 }}>
          <FormControl fullWidth>
            <InputLabel shrink>Upload</InputLabel>
            <label htmlFor="upload-file">
              <TextField
                accept=".pdf"
                id="upload-file"
                type="file"
                {...register("file")}
                onChange={handleFileChange}
                style={{ display: "none" }}
                error={errors?.file ? true : false}
              />
              <UploadButton
                variant="outlined"
                component="span"
                className="upload-btn"
              >
                <img src={UploadIcon} alt="upload" />
                {selectedFile && selectedFile.name
                  ? selectedFile.name
                  : "Upload File"}
              </UploadButton>
            </label>
            {errors.file && (
              <Typography variant="caption" color="error">
                {errors.file.message}
              </Typography>
            )}
          </FormControl>
        </Box>

        {/* Form Actions */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            mt: 4,
            mb: 2,
          }}
        >
          <Button
            variant="outlined"
            onClick={handleCancel}
            disabled={isPending}
            sx={{
              borderRadius: "4px",
              textTransform: "none",
            }}
            className="secondary-button"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isPending}
            sx={{
              borderRadius: "4px",
              backgroundColor: "#F97316",
              "&:hover": { backgroundColor: "#EA580C" },
              textTransform: "none",
            }}
          >
            {isPending ? "Submitting..." : "Submit"}
          </Button>
        </Box>
      </form>
    </FormDialog>
  );
};

export default AddAffidavitModal; 